import * as ko from 'knockout';

import i18n from '../i18n';
import { ListRequestParams } from '../api/request';
import { ListLoaderDelegate, ListLoader, delegateActions } from '../components/list_loader';
import { DimensionMeta } from '../models/dimension_meta';
import * as dimensionMetasApi from '../api/dimension_metas';
import * as usersApi from '../api/users';
import { downloadBlob, updateLocationWithQueryString } from '../utils';
import {
  canCreateDimensionMeta,
  canDeleteDimensionMeta,
  canEditDimensionMeta,
  reactListTablesEnabled,
} from '../permissions';
import { BaseLoadingScreen } from './base_loading_screen';
import { ListHeaderAction } from '../components/list_header';
import { Action } from '../components/basic_widgets';
import { confirmDialog } from '../components/confirm_dialog';
import { ShowArchivedFilter } from '../components/show_archived_filter';
import { session } from '../session';
import { translate } from '../i18n_text';
import { GridView, refreshRows, pageParams } from '../utils/grid_view';
import { ARCHIVED_HIDDEN_FILTER } from '@/lib/gridViewState';
import {
  DimensionsListPage,
  DimensionsListProps,
  DimensionsListQuery,
  DimensionsListRow,
} from 'react-islands';

let template = require('raw-loader!../../templates/dimension_metas.html').default;

class DimensionMetasScreen
  extends BaseLoadingScreen
  implements ListLoaderDelegate<dimensionMetasApi.DimensionMetaData, DimensionMeta>
{
  showArchivedFilter: ShowArchivedFilter;
  exporting = ko.observable(false);
  canCreate = false;
  canEdit = false;
  canDelete = false;
  listActions = ko.observableArray<ListHeaderAction>();
  private loader: ListLoader<dimensionMetasApi.DimensionMetaData, DimensionMeta>;

  private nameFilter = ko.observable('').extend({ throttle: 300 });
  searchPlaceholder = i18n.t('Search dimensions')();

  allowItemsPerLoadDisplay = true;

  newDimensionsTable = reactListTablesEnabled();

  private subscriptions: KnockoutSubscription[] = [];

  private view = new GridView('dimensions');

  tableProps = ko.pureComputed<DimensionsListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
  }));

  constructor(params: { filters: { name_search: string } }) {
    super();
    this.showArchivedFilter = new ShowArchivedFilter('dimension', () => {
      if (this.loader) this.loader.forceLoad();
    });

    if (this.newDimensionsTable) {
      this.view.load(params.filters, { firstVisit: { filter: ARCHIVED_HIDDEN_FILTER } });
    } else {
      this.nameFilter(params.filters.name_search || '');
    }

    this.listActions.push({
      title: i18n.t('Export')(),
      icon: 'file_download',
      onClick: this.exportAll,
      loading: this.exporting,
    });

    let promise = usersApi.me().then(() => {
      this.canCreate = canCreateDimensionMeta();
      this.canEdit = canEditDimensionMeta();
      this.canDelete = canDeleteDimensionMeta();

      if (this.canCreate) {
        this.listActions.splice(0, 0, {
          title: i18n.t('Add')(),
          icon: 'add_circle',
          href: '/dimensions/new/',
          tooltipTitle: i18n.t('Add dimension')(),
        });
      }
    });
    this.loadedAfter(promise);
  }

  onReady(loader: ListLoader<dimensionMetasApi.DimensionMetaData, DimensionMeta>) {
    this.loader = loader;
    this.subscriptions.push(this.nameFilter.subscribe(() => this.loader.forceLoad()));
  }

  fetch(params: ListRequestParams) {
    const filter = {
      name_search: this.nameFilter(),
      archived: this.showArchivedFilter.on() ? null : false,
    };
    updateLocationWithQueryString(filter);
    return dimensionMetasApi.listCustom({ ...params, ...filter });
  }

  private fetchPage = (query: DimensionsListQuery): Promise<DimensionsListPage> =>
    this.view.fetchPage(
      query,
      (options) =>
        dimensionMetasApi.listCustom(
          // Archived is a filter condition; `archived` is omitted so the endpoint returns both.
          pageParams(query, 'name_search'),
          options
        ),
      this
    );

  toRow = (dimensionMeta: DimensionMeta): DimensionsListRow => ({
    id: dimensionMeta.id(),
    name: translate(dimensionMeta.nameJson()),
    // '' when this dimension has no records this user may open, which makes the name plain text and
    // hides the Records button.
    recordsUrl: dimensionMeta.canViewAndEditRecords()
      ? session.toTenantPath(dimensionMeta.recordsUrl())
      : '',
    archived: !!dimensionMeta.archived(),
    actions: delegateActions(this, dimensionMeta, this.view.reload),
  });

  private applyChange = () => refreshRows(this.newDimensionsTable, this.view, this.loader);

  instantiate(dimensionMetaData: dimensionMetasApi.DimensionMetaData) {
    return new DimensionMeta(dimensionMetaData);
  }

  remove(id: string) {
    return dimensionMetasApi.remove(id);
  }

  getEditUrl(dimensionMeta: DimensionMeta): string {
    return this.canEdit ? dimensionMeta.editUrl() : '';
  }

  canRemove(dimensionMeta: DimensionMeta) {
    return this.canDelete && dimensionMeta.canRemove();
  }

  getActions(dimensionMeta: DimensionMeta): Action[] {
    if (!this.canEdit || !dimensionMeta.canRemove()) {
      return [];
    }

    if (dimensionMeta.archived()) {
      return this.getUnarchiveAction(dimensionMeta);
    }

    return this.getArchiveAction(dimensionMeta);
  }

  private getUnarchiveAction(dimensionMeta: DimensionMeta) {
    return [
      {
        icon: 'unarchive',
        title: i18n.t('Unarchive')(),
        cssClass: '',
        onClick: () => {
          confirmDialog(i18n.t('Unarchive dimension?')(), [
            i18n.t('Unarchived dimensions will be visible and selectable.')(),
            i18n.t('Are you sure you want to continue?')(),
          ]).then(() => this.unarchive(dimensionMeta));
        },
      },
    ];
  }

  private getArchiveAction(dimensionMeta: DimensionMeta) {
    return [
      {
        icon: 'archive',
        title: i18n.t('Archive')(),
        cssClass: '',
        onClick: () => {
          confirmDialog(i18n.t('Archive dimension?')(), [
            i18n.t('Archived dimensions will no longer be visible or selectable for new trials.')(),
            i18n.t('Are you sure you want to continue?')(),
          ]).then(() => this.archive(dimensionMeta));
        },
      },
    ];
  }

  private unarchive(dimensionMeta: DimensionMeta) {
    dimensionMetasApi.unarchive(dimensionMeta.id()).then(() => {
      this.applyChange();
    });
  }

  private archive(dimensionMeta: DimensionMeta) {
    dimensionMetasApi.archive(dimensionMeta.id()).then(() => {
      this.applyChange();
    });
  }

  exportAll = () => {
    this.exporting(true);

    dimensionMetasApi
      .exportAll()
      .then((data) => {
        this.exporting(false);
        downloadBlob(data, 'dimensions.xlsx');
      })
      .catch(() => {
        this.exporting(false);
      });
  };

  dispose() {
    this.subscriptions.forEach((s) => s.dispose());
    this.subscriptions = [];
  }
}

export let dimensionMetas = {
  name: 'dimension-metas',
  viewModel: DimensionMetasScreen,
  template: template,
};

ko.components.register(dimensionMetas.name, dimensionMetas);
